// apps/share-web/src/offline/offline-store.ts
//
// Доменный слой офлайна: «библиотека этого человека» и «слова одной песни».
// Ни одна строка здесь не знает, что под ней IndexedDB — под ней KvBackend.
//
// Хранилище создаётся НА ЧЕЛОВЕКА (`createOfflineStore(kv, userId)`), а не на
// приложение: userId уезжает в каждый ключ по построению, и «забыть его
// подставить» в отдельном вызове физически негде: даже `del`, которому список
// ключей приходит снаружи, отбрасывает всё, что не под этим человеком.

import type { KvBackend } from './kv-backend.js';
import { makeKey, userPrefix } from './offline-keys.js';
import type { TeamLibrary } from '../library.js';

export interface CachedLibrary {
  teams: TeamLibrary[];
  /** Когда снимок приехал с сервера. Источник строки свежести. */
  savedAt: string;
}

export interface CachedWords {
  /** `updatedAt` песни на момент сохранения — им сверяется, не устарели ли слова. */
  updatedAt: string;
  payload: unknown;
}

/**
 * Состав ОДНОГО сетлиста — тело `GET /api/setlists/:id` (задача C5 волны G).
 * Та же форма конверта, что у `CachedWords`, и по той же причине: `updatedAt`
 * — не из самого тела, а из `SetlistSummary.updatedAt` библиотеки на момент
 * сохранения (offline/setlist-offline.ts, `isStale`) — им сверяется докачка,
 * тем же приёмом, что у `SongTextSummary.updatedAt` для слов.
 */
export interface CachedSetlist {
  updatedAt: string;
  payload: unknown;
}

/**
 * Дефект 2 живой приёмки волны G: владелец продукта, дословно — «появилась
 * кнопка Connection Lost, Offline. И сверху появилось типа «войдите» — хотя
 * должен был остаться аккаунт». Шапка (`ui/app-header.ts`) читала сетевой
 * отказ `fetchMe` как «не вошёл» и рисовала «Sign in» вошедшему человеку.
 * Минимум для честного офлайн-показа шапки — email (инициалы аватара,
 * подпись в меню) и команда (пилюля рядом с аватаром, имя в шапке меню).
 * `userId`/`isAdmin` НЕ кэшируются: `userId` шапкой не читается вовсе, а
 * `isAdmin` без сети не подтвердить — показать админский пункт меню по
 * устаревшей догадке хуже, чем скрыть его до возврата сети (см.
 * ui/app-header.ts, `loadCachedMe`).
 */
export interface CachedMe {
  email: string | null;
  teams: Array<{ id: string; name: string; role: string }>;
}

export interface OfflineStore {
  loadLibrary(): Promise<CachedLibrary | null>;
  saveLibrary(teams: TeamLibrary[], savedAt: string): Promise<void>;
  /** Кэш вошедшего для офлайн-показа шапки (дефект 2 волны G). `null` — ещё
   *  не писали (свежий телефон) либо запись оказалась чужой формы. */
  loadMe(): Promise<CachedMe | null>;
  saveMe(me: CachedMe): Promise<void>;
  loadWords(teamId: string, songTextId: string): Promise<CachedWords | null>;
  saveWords(teamId: string, songTextId: string, words: CachedWords): Promise<void>;
  /**
   * Снести слова ОДНОЙ песни. Нужен там, где сервер сказал, что этих слов у
   * человека больше нет (403/404 на странице слов — offline/words-resolve.ts):
   * сверка после успешного `/api/library` туда не достаёт, потому что
   * успешного ответа в этом сценарии как раз и нет.
   *
   * Ключ строится ТЕМ ЖЕ `makeKey`, что и запись, а не отбирается из
   * `cachedKeys()` по хвосту: `endsWith(songTextId)` снёс бы вместе с 's1'
   * ещё и 'xs1'. Публикации слов адресуются id'ами десктопа, и совпадение
   * хвоста — не экзотика.
   */
  delWords(teamId: string, songTextId: string): Promise<void>;
  /** Состав одного сетлиста (задача C5 волны G) — зеркало loadWords/saveWords
   *  по кинду 'setlist'. */
  loadSetlist(teamId: string, setlistId: string): Promise<CachedSetlist | null>;
  saveSetlist(teamId: string, setlistId: string, setlist: CachedSetlist): Promise<void>;
  /** Снести состав ОДНОГО сетлиста — тем же случаем, что у `delWords`: сервер
   *  сказал, что этого сетлиста у человека больше нет (например, ветка
   *  `notFound`/`needsAuth` `fetchPublishedSetlist`, setlist.ts), а сверка по
   *  успешному `/api/library` до этого сценария не достаёт. */
  delSetlist(teamId: string, setlistId: string): Promise<void>;
  /** Все ключи ЭТОГО человека — вход для сверки. */
  cachedKeys(): Promise<string[]>;
  del(keys: string[]): Promise<void>;
  /** Выход из аккаунта и кнопка «стереть офлайн-данные». */
  clearUser(): Promise<void>;
}

// ВЕРСИЯ ФОРМЫ ЗАПИСИ — внутри значения, а не в номере версии базы.
//
// idb-backend намеренно держит версию базы, чтобы переход на новую форму не
// делался стиранием (иначе человек теряет офлайн ровно тогда, когда обновил
// приложение перед служением). Прямое следствие: однажды из хранилища
// приедет запись ПРЕЖНЕЙ формы. Слепой каст `as CachedLibrary` отдал бы её
// вьюхе, и та упала бы на `cached.teams.map(...)` — на телефоне, в офлайне,
// то есть там, где чинить нечем.
//
// Поэтому значение едет в конверте с отметкой формы, и читается только своя.
// Отметка — не валидация схемы: она не проверяет поля, она заявляет, что их
// смысл не менялся. Меняешь форму любой из записей — увеличивай SHAPE.
//
// Задача C5 волны G заводит четвёртую запись (`CachedSetlist`, kind
// 'setlist') — и НЕ трогает SHAPE. Отметка охраняет форму КОНВЕРТА
// (`{shape, value}`), а не форму значения внутри: значение конверт вообще не
// разбирает, `unpack` возвращает его как есть под типом, который называет
// вызывающий (`loadWords`/`loadMe`/теперь `loadSetlist`). Новый КИНД записи
// со своим НОВЫМ значением — не изменение смысла уже лежащих записей
// library/words/me: они как читались под SHAPE=1, так и читаются. Бампать
// было бы нужно, если бы менялась форма СУЩЕСТВУЮЩЕЙ записи (например,
// `CachedWords.payload` перестал бы быть тем, чем был) — тогда старая запись
// стала бы опасной для чтения под прежней меткой, и `unpack` обязан был бы
// её отвергнуть. Здесь этого нет: `CachedSetlist` — новый ключ пространства,
// а не переосмысление старого.
const SHAPE = 1;

interface Envelope { shape: number; value: unknown }

function pack(value: unknown): Envelope {
  return { shape: SHAPE, value };
}

function unpack<T>(raw: unknown): T | null {
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Partial<Envelope>;
  // Чужая или отсутствующая отметка — это «кэша нет», а не «прочитать как
  // есть»: пустой экран честнее падения, и следующий успешный ответ его
  // заполнит.
  if (e.shape !== SHAPE) return null;
  return (e.value ?? null) as T | null;
}

/** Библиотека лежит одной записью на человека: команда в ключе — пустая
 *  строка, потому что снимок приходит целиком одним ответом, и резать его по
 *  командам значило бы уметь собрать обратно то, чего сервер не обещал. */
const LIBRARY_TEAM = '';

export function createOfflineStore(kv: KvBackend, userId: string): OfflineStore {
  const libKey = makeKey(userId, LIBRARY_TEAM, 'library', '');
  // Команда в ключе пустая — тем же приёмом, что у `libKey` выше: «вошедший»
  // не привязан к одной команде, запись одна на человека.
  const meKey = makeKey(userId, LIBRARY_TEAM, 'me', '');
  return {
    async loadLibrary() {
      return unpack<CachedLibrary>(await kv.get(libKey));
    },
    async saveLibrary(teams, savedAt) {
      await kv.set(libKey, pack({ teams, savedAt } satisfies CachedLibrary));
    },
    async loadMe() {
      return unpack<CachedMe>(await kv.get(meKey));
    },
    async saveMe(me) {
      await kv.set(meKey, pack(me));
    },
    async loadWords(teamId, songTextId) {
      return unpack<CachedWords>(await kv.get(makeKey(userId, teamId, 'words', songTextId)));
    },
    async saveWords(teamId, songTextId, words) {
      await kv.set(makeKey(userId, teamId, 'words', songTextId), pack(words));
    },
    async delWords(teamId, songTextId) {
      await kv.del([makeKey(userId, teamId, 'words', songTextId)]);
    },
    async loadSetlist(teamId, setlistId) {
      return unpack<CachedSetlist>(await kv.get(makeKey(userId, teamId, 'setlist', setlistId)));
    },
    async saveSetlist(teamId, setlistId, setlist) {
      await kv.set(makeKey(userId, teamId, 'setlist', setlistId), pack(setlist));
    },
    async delSetlist(teamId, setlistId) {
      await kv.del([makeKey(userId, teamId, 'setlist', setlistId)]);
    },
    async cachedKeys() {
      return kv.keys(userPrefix(userId));
    },
    async del(keys) {
      // Личность подставляется и здесь — иначе утверждение в шапке («забыть
      // её физически негде») было бы неправдой: `del` единственный метод,
      // которому ключи приезжают снаружи, и чужой ключ в списке снёс бы
      // чужие слова. Фильтр в reconcile остаётся, но как второй эшелон.
      const mine = userPrefix(userId);
      await kv.del(keys.filter((k) => k.startsWith(mine)));
    },
    async clearUser() {
      await kv.del(await kv.keys(userPrefix(userId)));
    },
  };
}
